<script setup lang="ts">
/**
 * PCard — conteneur éditorial pour grouper du contenu.
 *
 * Revelio mandate : flat (pas d'ombre par défaut), bordure 1px line, radius 2px,
 * padding 20px (p-5). La variante `inverse` retourne la card en sombre (bg ink)
 * pour signaler un KPI principal ou une zone d'identité forte (équipe, score).
 *
 * Slots :
 *  - header  : titre + actions optionnelles (eyebrow + h2/h3)
 *  - default : contenu principal
 *  - footer  : actions de bas de carte
 *
 * Props :
 *  - padded   : applique padding par défaut (false pour cards contenant une
 *               PTable ou une image full-bleed)
 *  - inverse  : variante dark (bg ink, text surface) — pour KPI hero
 *  - hoverable: hover état (border-strong + bg surface-alt) — pour les cards
 *               qui sont en réalité des router-link
 */
interface Props {
    padded?: boolean
    inverse?: boolean
    hoverable?: boolean
    /** Conservé pour compat — utilise shadow-sm. Revelio préfère hoverable. */
    elevated?: boolean
}

withDefaults(defineProps<Props>(), {
    padded: true,
    inverse: false,
    hoverable: false,
    elevated: false,
})
</script>

<template>
    <section
        :class="[
            'p-card',
            {
                'p-card--padded': padded,
                'p-card--inverse': inverse,
                'p-card--hoverable': hoverable,
                'p-card--elevated': elevated,
            },
        ]"
    >
        <header v-if="$slots.header" class="p-card__header">
            <slot name="header" />
        </header>
        <div class="p-card__body">
            <slot />
        </div>
        <footer v-if="$slots.footer" class="p-card__footer">
            <slot name="footer" />
        </footer>
    </section>
</template>

<style scoped>
.p-card {
    background: var(--p-color-surface);
    border: 1px solid var(--p-color-line);
    border-radius: var(--p-radius-sm);
    overflow: hidden;
    transition:
        border-color var(--p-motion-fast) var(--p-ease-out),
        background var(--p-motion-fast) var(--p-ease-out);
}

.p-card--elevated {
    box-shadow: var(--p-shadow-sm);
}

.p-card--hoverable {
    cursor: pointer;
}
.p-card--hoverable:hover {
    border-color: var(--p-color-ink-3);
    background: color-mix(in srgb, var(--p-color-surface-alt) 40%, var(--p-color-surface) 60%);
}

.p-card--inverse {
    background: var(--p-color-ink);
    color: var(--p-color-surface);
    border-color: var(--p-color-ink);
}
.p-card--inverse.p-card--hoverable:hover {
    background: var(--p-color-ink-2);
    border-color: var(--p-color-ink-2);
}

.p-card__header {
    padding: 16px 20px;
    border-bottom: 1px solid var(--p-color-line);
    font-weight: var(--p-font-weight-medium);
}
.p-card--inverse .p-card__header {
    border-bottom-color: var(--p-color-ink-2);
}

.p-card--padded .p-card__body {
    padding: 20px;
}

.p-card__footer {
    padding: 12px 20px;
    border-top: 1px solid var(--p-color-line);
    display: flex;
    gap: var(--p-space-3);
    justify-content: flex-end;
}
.p-card--inverse .p-card__footer {
    border-top-color: var(--p-color-ink-2);
}
</style>
